import React, { useCallback } from 'react';

import { defineMessages, FormattedMessage } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import Heading from '@atlaskit/heading';
import WorkItemsIcon from '@atlaskit/icon/core/work-items';
import { Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import type { SendAnalyticsEvent } from '@atlassian/conversation-assistant-instrumentation/types/analytics';
import type { DraftWorkItem } from '@atlassian/jira-create-work-items/useAiCreateProjectContext/types.tsx';

import type { PreviewCardProps } from '../../../../common/types';
import { BasePreviewCard } from '../../../../common/ui/base-preview-card';
import type { GicModalProps } from '../../../../common/ui/gic-modal';
import { ProjectSelector } from '../../../../common/ui/project-selector';

import type { jiraMultiWorkItemCard_AtlassianConversationAssistantPreviewCard_JiraMultiWorkItemPreviewCard$key as MultiWorkItemCardKey } from './__generated__/jiraMultiWorkItemCard_AtlassianConversationAssistantPreviewCard_JiraMultiWorkItemPreviewCard.graphql';
import { WorkItemsTable } from './work-items-table';

// Component types
type JiraMultiWorkItemPreviewCardProps = PreviewCardProps & {
	project: MultiWorkItemCardKey;
	projectKey: string;
	draftWorkItems: DraftWorkItem[];
	hideInlineButtons: boolean;
	previewFooterActions?: React.ReactNode;
	isCreated: boolean;
	onDeclineWorkItem: (args: { invocationId: string }) => void;
	onInlineAccept: (args: { draftWorkItem: DraftWorkItem }) => Promise<void>;
	openWorkItemView: (createdIssueKey?: string) => void;
	gicEntryPointProps: GicModalProps;
	isStagingArea: boolean;
	selectedItems: Set<string>;
	onToggleSelect: (invocationId: string, newValue: boolean) => void;
	hasAnyCreatedItems: boolean;
	isCreating: boolean;
	sendAifcAnalyticsEvent?: SendAnalyticsEvent;
};

const messages = defineMessages({
	previewHeaderTitle: {
		id: 'ai-mate.conversation-assistant-preview-card.jira-preview-card.preview-header-title',
		description: 'The title of the multi work item preview card for the Jira AIFC experience',
		defaultMessage: 'Create work items',
	},
	openPreview: {
		id: 'ai-mate.conversation-assistant-preview-card.jira-preview-card.open-preview',
		description:
			'The label for the button to open the multi work item preview card for the Jira AIFC experience',
		defaultMessage: 'Open preview',
	},
	viewingNow: {
		id: 'ai-mate.conversation-assistant-preview-card.jira-preview-card.viewing-now',
		description:
			'The label for the button to view the multi work item preview card for the Jira AIFC experience',
		defaultMessage: 'Viewing now',
	},
});

/**
 * Renders the BasePreviewCard for the Jira AIFC experience when there are multiple work items to create.
 *
 * @param cloudId - The cloud ID of the Jira instance.
 * @param isSelected - Whether the preview card is selected.
 * @param isEnabled - True when it is the last message in the conversation and the smart create modal is not open.
 * @param onPreviewClick - The function to call when the preview card is clicked.
 * @param actions - The actions to render in the preview card.
 * @returns The BasePreviewCard for the Jira AIFC experience.
 */

export const JiraMultiWorkItemPreviewCard = ({
	cloudId,
	project,
	projectKey,
	draftWorkItems,
	isCreated,
	isSelected,
	isEnabled,
	hideInlineButtons,
	onDeclineWorkItem,
	onInlineAccept,
	previewFooterActions,
	openWorkItemView,
	gicEntryPointProps,
	isStagingArea,
	onProjectChange,
	selectedItems,
	onToggleSelect,
	hasAnyCreatedItems,
	isCreating,
	sendAifcAnalyticsEvent,
}: JiraMultiWorkItemPreviewCardProps): React.JSX.Element => {
	const workItemsTableRef = useFragment<MultiWorkItemCardKey>(
		graphql`
			fragment jiraMultiWorkItemCard_AtlassianConversationAssistantPreviewCard_JiraMultiWorkItemPreviewCard on JiraProject {
				id
				name
				avatar {
					xsmall
				}
				...workItemsTable_AtlassianConversationAssistantPreviewCard_project
			}
		`,
		project,
	);

	// Project info of current project to initially be displayed in the project selector
	const projectId = workItemsTableRef.id;
	const projectName = workItemsTableRef.name;
	const projectIcon = workItemsTableRef.avatar?.xsmall ?? undefined;

	const hideFooterActions = isSelected || !isEnabled || isCreated || hasAnyCreatedItems;

	const setProject = useCallback(
		({ projectKey: pk }: { projectKey: string }) => onProjectChange(pk),
		[onProjectChange],
	);

	return (
		<BasePreviewCard
			testId="conversation-assistant-preview-card.jira-multi-work-item-preview-card"
			previewProductLogo={
				<WorkItemsIcon
					color={token('color.icon.accent.blue')}
					label={
						FeatureGates.getExperimentValue(
							'jira-aifc-a11y-fixes-preview-card',
							'isEnabled',
							false,
							{
								fireExperimentExposure: false,
							},
						)
							? ''
							: 'Work items'
					}
				/>
			}
			isSelected={isSelected}
			isEnabled={isEnabled}
			isCreated={isCreated}
			previewHeaderTitle={
				FeatureGates.getExperimentValue('jira-aifc-a11y-fixes-preview-card', 'isEnabled', false, {
					fireExperimentExposure: false,
				}) ? (
					<Heading as="h3" size="xsmall">
						<FormattedMessage {...messages.previewHeaderTitle} />
					</Heading>
				) : (
					<Text size="medium" weight="bold">
						<FormattedMessage {...messages.previewHeaderTitle} />
					</Text>
				)
			}
			previewContent={
				<>
					{FeatureGates.getExperimentValue(
						'jira-aifc-project-selector-in-chat-exp',
						'isEnabled',
						false,
					) ? (
						<ProjectSelector
							isDisabled={!isEnabled || isCreated}
							siteId={cloudId}
							selectedProject={{ label: projectName, value: projectId, projectKey }}
							selectedProjectIconUrl={projectIcon}
							setProject={setProject}
						/>
					) : null}
					<WorkItemsTable
						project={workItemsTableRef}
						draftWorkItems={draftWorkItems}
						onDeclineWorkItem={onDeclineWorkItem}
						hideInlineButtons={hideInlineButtons}
						openWorkItemView={openWorkItemView}
						gicEntryPointProps={gicEntryPointProps}
						onInlineAccept={onInlineAccept}
						isStagingArea={isStagingArea}
						selectedItems={selectedItems}
						onToggleSelect={onToggleSelect}
						hasAnyCreatedItems={hasAnyCreatedItems}
						isCreating={isCreating}
						sendAifcAnalyticsEvent={sendAifcAnalyticsEvent}
					/>
				</>
			}
			previewFooterActions={hideFooterActions ? null : previewFooterActions}
		/>
	);
};
